import React, { useState, useEffect } from 'react';
import commaizeInt from 'js/utils/commaize';

import { Author } from 'js/search/react/canonical_work_results';

interface AuthorPopoverProps {
  author: Author;
  fetchActorData: (author: Author) => void;
  followUser: (id: number | string, callback: () => void) => void;
  unfollowUser: (id: number | string, callback: () => void) => void;
  addClickTracking: (url: string) => string;
  loggedIn?: boolean;
  reduceTopPosition?: boolean;
  isOffsiteAuthor?: boolean;
}

const AuthorPopover: React.FC<AuthorPopoverProps> = ({
  author,
  fetchActorData,
  followUser,
  unfollowUser,
  addClickTracking,
  loggedIn = true,
  reduceTopPosition = false,
  isOffsiteAuthor = false,
}) => {
  const [fetchingFollow, setFetchingFollow] = useState(false);

  useEffect(() => {
    if (
      author.viewCount == null ||
      author.workCount == null ||
      author.followerCount == null
    ) {
      fetchActorData(author);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFollowButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!fetchingFollow) {
      setFetchingFollow(true);
      if (author.isFollowing) {
        unfollowUser(author.id, () => setFetchingFollow(false));
      } else {
        followUser(author.id, () => setFetchingFollow(false));
      }
    }
  };

  const followingButton = () => {
    let icon;
    let text;
    if (author.isFollowing) {
      text = 'Following';
      icon = <i className="fa fa-check work-card--author-popover-icon" />;
    } else {
      text = 'Follow';
      icon = <i className="fa fa-plus work-card--author-popover-icon" />;
    }

    if (fetchingFollow) {
      icon = (
        <i className="fa fa-spinner fa-spin work-card--author-popover-icon" />
      );
    }

    return (
      <div
        className="work-card--author-popover-button"
        onClick={onFollowButtonClick}
      >
        {icon}
        {text}
      </div>
    );
  };

  const messageButton = () => (
    <div
      className="work-card--author-popover-button"
      data-send-message
      data-recipient-id={author.id}
    >
      <i className="fa fa-comment work-card--author-popover-icon" />
      Message
    </div>
  );

  const department = author.department?.name;
  const university = author.department?.university?.name;

  // For ownerless works, external authors don't have URLs - shouldn't reach here
  // but guard against it just in case
  if (!author.url) {
    return null;
  }

  return (
    <div
      className="work-card--author-popover-outer-wrapper"
      style={reduceTopPosition ? { top: '20px' } : {}}
    >
      <div className="work-card--author-popover-inner-wrapper">
        <div className="work-card--popover-outer-header">
          <a href={addClickTracking(author.url)} className="work-card--link">
            <img
              className="work-card--profile-image u-mr2x"
              src={author.photo}
            />
          </a>
          <div className="work-card--popover-expand-wrapper">
            <a
              href={addClickTracking(author.url)}
              className="work-card--link-underline"
            >
              <div className="work-card--popover-name">
                {author.displayName}
              </div>
            </a>
            {!isOffsiteAuthor && (
              <div className="work-card--author-popover-action-wrapper">
                {loggedIn && followingButton()}
                {loggedIn && messageButton()}
              </div>
            )}
          </div>
        </div>
        <div className="work-card--author-popover-affiliation-wrapper">
          {!!university && (
            <div className="work-card--inline-wrapper">
              <div className="work-card--author-popover-affiliation">
                {university}
              </div>
              {!!department && <div className="work-card--comma">,</div>}
            </div>
          )}
          {!!department && (
            <div className="work-card--inline-wrapper">
              <div className="work-card--author-popover-affiliation">
                {department}
              </div>
            </div>
          )}
        </div>
        {!isOffsiteAuthor && (
          <div className="work-card--author-popover-stats-outer-wrapper">
            <div className="work-card--author-popover-stat-wrapper">
              <div className="work-card--author-popover-stat-title">Papers</div>
              {author.workCount != null ? (
                <div>{commaizeInt(author.workCount)}</div>
              ) : (
                <i className="fa fa-spinner fa-spin u-tcGrayDark u-fs16" />
              )}
            </div>
            <div className="work-card--author-popover-stat-wrapper">
              <div className="work-card--author-popover-stat-title">Views</div>
              {author.viewCount != null ? (
                <div>{commaizeInt(author.viewCount)}</div>
              ) : (
                <i className="fa fa-spinner fa-spin u-tcGrayDark u-fs16" />
              )}
            </div>
            <div className="work-card--author-popover-stat-wrapper">
              <div className="work-card--author-popover-stat-title">
                Followers
              </div>
              {author.followerCount != null ? (
                <div>{commaizeInt(author.followerCount)}</div>
              ) : (
                <i className="fa fa-spinner fa-spin u-tcGrayDark u-fs16" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorPopover;
